/**
 * Real-time validation service for registration forms
 * Provides debounced validation for username and email availability
 */

import { API_BASE_URL } from '../constants';
import { validateStrictEmail } from './userInputValidation';

// EMERGENCY FIX: Force relative URLs on Vercel domains
const FORCE_API_BASE_URL = typeof window !== 'undefined' &&
 (window.location.hostname.includes('vercel.app') || window.location.hostname.includes('vercel.com'))
 ? '' // Force relative URLs on Vercel
 : API_BASE_URL;

// Relative-URL override stays. The 2025-01 emergency debug log was removed.

export interface ValidationResult {
 isValid: boolean;
 isChecking: boolean;
 message: string;
 type: 'success' | 'error' | 'info' | 'checking';
 userId?: number; // User ID for self-transfer prevention
 sponsorName?: string; // v4.4.0: Sponsor display name for identity confirmation
}

export interface DuplicateCheckResponse {
 success: boolean;
 emailDuplicate?: boolean;
 usernameDuplicate?: boolean;
 sponsorExists?: boolean | null;
 sponsorUserId?: number; // User ID for self-transfer prevention
 sponsorName?: string; // v4.4.0: Sponsor display name for identity confirmation
 sponsorError?: string;
 error?: string;
}

class RealtimeValidationService {
 private debounceTimers: Map<string, NodeJS.Timeout> = new Map();
 private validationCache: Map<string, ValidationResult> = new Map();
 private readonly DEBOUNCE_DELAY = 800; // 800ms debounce delay

 // Track the latest requested value for each validation type to prevent race conditions
 private latestRequests: Map<string, string> = new Map();

 /**
 * Debounced username validation
 */
 async validateUsername(
 username: string,
 callback: (result: ValidationResult) => void
 ): Promise<void> {
 const validationType = 'username';
 const key = `username_${username}`;

 // Track latest requested value to prevent stale callbacks
 this.latestRequests.set(validationType, username);

 // v7.3.354: Clear ALL pending username timers, not just the one with this exact key.
 // Each keystroke produces a distinct key (username_j, username_jp, ...) so the previous
 // approach (clearing only the matching key) let multiple timers stack up. When the user
 // paused, all of them fired in a burst and tripped the /api/check-duplicates IP rate
 // limit (8/min). Mirrors the pattern already used in validateUserExists.
 this.debounceTimers.forEach((timer, timerKey) => {
 if (timerKey.startsWith('username_')) {
 clearTimeout(timer);
 this.debounceTimers.delete(timerKey);
 }
 });

 // Check cache first
 const cached = this.validationCache.get(key);
 if (cached && !cached.isChecking) {
 callback(cached);
 return;
 }

 // Show checking state immediately
 const checkingResult: ValidationResult = {
 isValid: false,
 isChecking: true,
 message: 'Checking username availability...',
 type: 'checking'
 };
 callback(checkingResult);

 // Set debounced validation
 const timer = setTimeout(async () => {
 try {
 const result = await this.checkUsernameAvailability(username);
 this.validationCache.set(key, result);
 if (this.latestRequests.get(validationType) === username) {
 callback(result);
 }
 } catch (error) {
 if (this.latestRequests.get(validationType) === username) {
 const errorResult: ValidationResult = {
 isValid: false,
 isChecking: false,
 message: 'Error checking username availability',
 type: 'error'
 };
 callback(errorResult);
 }
 }
 this.debounceTimers.delete(key);
 }, this.DEBOUNCE_DELAY);

 this.debounceTimers.set(key, timer);
 }

 /**
 * Debounced user existence validation (for transfers, etc.)
 */
 async validateUserExists(
 username: string,
 callback: (result: ValidationResult) => void
 ): Promise<void> {
 const validationType = 'user_exists';
 const cacheKey = `user_exists_${username.toLowerCase()}`;

 // Track the latest requested username to prevent race conditions
 this.latestRequests.set(validationType, username.toLowerCase());

 // Clear ALL existing timers for user_exists to prevent old requests from completing
 this.debounceTimers.forEach((timer, key) => {
 if (key.startsWith('user_exists_')) {
 clearTimeout(timer);
 this.debounceTimers.delete(key);
 }
 });

 // Check cache first (case-insensitive)
 const cached = this.validationCache.get(cacheKey);
 if (cached && !cached.isChecking) {
 callback(cached);
 return;
 }

 // Show checking state immediately
 const checkingResult: ValidationResult = {
 isValid: false,
 isChecking: true,
 message: 'Checking username...',
 type: 'checking'
 };
 callback(checkingResult);

 // Set debounced validation
 const timer = setTimeout(async () => {
 try {
 // Check if this is still the latest request (prevents race condition)
 if (this.latestRequests.get(validationType) !== username.toLowerCase()) {
 console.log(`Skipping stale validation for "${username}" - newer request exists`);
 return; // Skip - a newer request has been made
 }

 const result = await this.checkUserExists(username);
 this.validationCache.set(cacheKey, result);

 // Double-check we're still the latest before calling callback
 if (this.latestRequests.get(validationType) === username.toLowerCase()) {
 callback(result);
 }
 } catch (error) {
 // Only callback if still the latest request
 if (this.latestRequests.get(validationType) === username.toLowerCase()) {
 const errorResult: ValidationResult = {
 isValid: false,
 isChecking: false,
 message: 'Error checking username',
 type: 'error'
 };
 callback(errorResult);
 }
 }
 this.debounceTimers.delete(cacheKey);
 }, this.DEBOUNCE_DELAY);

 this.debounceTimers.set(cacheKey, timer);
 }

 /**
 * Debounced sponsor username validation
 */
 async validateSponsorUsername(
 sponsorUsername: string,
 callback: (result: ValidationResult) => void,
 currentUsername?: string
 ): Promise<void> {
 const validationType = 'sponsor';
 // v4.4.0: case-insensitive cache key — server uses ilike, so 'JohnDoe' and
 // 'johndoe' resolve to the same row and must share a cache entry.
 const normalizedValue = sponsorUsername.toLowerCase();
 const key = `sponsor_${normalizedValue}`;

 // Track latest requested value to prevent stale callbacks
 this.latestRequests.set(validationType, normalizedValue);

 // v7.3.354: Clear ALL pending sponsor timers (not just this exact key) to prevent
 // request stacking that hammers /api/check-duplicates and trips the IP rate limit.
 this.debounceTimers.forEach((timer, timerKey) => {
 if (timerKey.startsWith('sponsor_')) {
 clearTimeout(timer);
 this.debounceTimers.delete(timerKey);
 }
 });

 // Clear any cached results for partial matches to ensure fresh validation
 // This fixes the bug where users need to retype the last character
 for (const [cacheKey] of this.validationCache.entries()) {
 if (cacheKey.startsWith('sponsor_') && cacheKey !== key) {
 // Clear cache for other sponsor usernames to prevent stale results
 this.validationCache.delete(cacheKey);
 }
 }

 // Check cache first (only for exact match)
 const cached = this.validationCache.get(key);
 if (cached && !cached.isChecking) {
 callback(cached);
 return;
 }

 // v4.4.0: do NOT show "Checking..." on every keystroke. Wait until the
 // debounce window settles before any UI feedback so partial / mid-typing
 // values never produce a flash of "checking" state. The previous result
 // (if any) stays on screen until the new lookup actually fires.
 const timer = setTimeout(async () => {
 // Only NOW signal that we are actually checking (debounce settled).
 if (this.latestRequests.get(validationType) === normalizedValue) {
 callback({
 isValid: false,
 isChecking: true,
 message: 'Checking sponsor username...',
 type: 'checking'
 });
 }
 try {
 const result = await this.checkSponsorUsernameExists(sponsorUsername, currentUsername);
 this.validationCache.set(key, result);
 if (this.latestRequests.get(validationType) === normalizedValue) {
 callback(result);
 }
 } catch (error) {
 if (this.latestRequests.get(validationType) === normalizedValue) {
 const errorResult: ValidationResult = {
 isValid: false,
 isChecking: false,
 message: 'Error checking sponsor username',
 type: 'error'
 };
 callback(errorResult);
 }
 }
 this.debounceTimers.delete(key);
 }, this.DEBOUNCE_DELAY);

 this.debounceTimers.set(key, timer);
 }

 /**
 * Debounced email validation
 */
 async validateEmail(
 email: string,
 callback: (result: ValidationResult) => void
 ): Promise<void> {
 const validationType = 'email';
 const key = `email_${email}`;

 // Track latest requested value to prevent stale callbacks
 this.latestRequests.set(validationType, email);

 // v7.3.354: Clear ALL pending email timers (not just this exact key) to prevent
 // request stacking that hammers /api/check-duplicates and trips the IP rate limit.
 this.debounceTimers.forEach((timer, timerKey) => {
 if (timerKey.startsWith('email_')) {
 clearTimeout(timer);
 this.debounceTimers.delete(timerKey);
 }
 });

 // Same rules as account creation, so a rejected address never receives a code.
 const format = validateStrictEmail(email);
 if (!format.valid) {
 const invalidResult: ValidationResult = {
 isValid: false,
 isChecking: false,
 message: format.error || 'Please enter a valid email address',
 type: 'error'
 };
 callback(invalidResult);
 return;
 }

 // Check cache first
 const cached = this.validationCache.get(key);
 if (cached && !cached.isChecking) {
 callback(cached);
 return;
 }

 // Show checking state immediately
 const checkingResult: ValidationResult = {
 isValid: false,
 isChecking: true,
 message: 'Checking email availability...',
 type: 'checking'
 };
 callback(checkingResult);

 // Set debounced validation
 const timer = setTimeout(async () => {
 try {
 const result = await this.checkEmailAvailability(email);
 this.validationCache.set(key, result);
 if (this.latestRequests.get(validationType) === email) {
 callback(result);
 }
 } catch (error) {
 if (this.latestRequests.get(validationType) === email) {
 const errorResult: ValidationResult = {
 isValid: false,
 isChecking: false,
 message: 'Error checking email availability',
 type: 'error'
 };
 callback(errorResult);
 }
 }
 this.debounceTimers.delete(key);
 }, this.DEBOUNCE_DELAY);

 this.debounceTimers.set(key, timer);
 }

 /**
 * Check username availability via API
 */
 private async checkUsernameAvailability(username: string): Promise<ValidationResult> {
 if (!username || username.trim().length < 2) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username must be at least 2 characters long',
 type: 'error'
 };
 }

 if (username.trim().length > 25) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username must be 25 characters or less',
 type: 'error'
 };
 }

 // Check for invalid characters - only allow letters, numbers, and underscores
 if (!/^[a-zA-Z0-9_]+$/.test(username.trim())) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username can only contain letters, numbers, and underscores',
 type: 'error'
 };
 }

 try {
 const response = await fetch(`${FORCE_API_BASE_URL}/api/check-duplicates`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ username: username.trim() }),
 });

 const data: DuplicateCheckResponse = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Server error');
 }

 if (data.usernameDuplicate) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username is already taken',
 type: 'error'
 };
 }

 return {
 isValid: true,
 isChecking: false,
 message: 'Username is available',
 type: 'success'
 };
 } catch (error) {
 console.error('Username validation error:', error);
 throw error;
 }
 }

 /**
 * Check if user exists via API (for transfers, etc.)
 */
 private async checkUserExists(username: string): Promise<ValidationResult> {
 if (!username || username.trim().length < 2) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username must be at least 2 characters long',
 type: 'error'
 };
 }

 if (username.trim().length > 25) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username must be 25 characters or less',
 type: 'error'
 };
 }

 try {
 const response = await fetch(`${FORCE_API_BASE_URL}/api/check-duplicates`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 sponsorUsername: username.trim() // Reuse sponsor check logic but with different messages
 }),
 });

 const data: DuplicateCheckResponse = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Server error');
 }

 if (data.sponsorExists === false) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Username not found. Please check the spelling.',
 type: 'error'
 };
 }

 if (data.sponsorExists === true) {
 return {
 isValid: true,
 isChecking: false,
 message: 'Username found',
 type: 'success',
 userId: data.sponsorUserId // Return userId for self-transfer prevention
 };
 }

 // This shouldn't happen, but handle it gracefully
 return {
 isValid: false,
 isChecking: false,
 message: 'Unable to verify username',
 type: 'error'
 };
 } catch (error) {
 console.error('User existence validation error:', error);
 throw error;
 }
 }

 /**
 * Check sponsor username exists via API
 */
 private async checkSponsorUsernameExists(sponsorUsername: string, currentUsername?: string): Promise<ValidationResult> {
 if (!sponsorUsername || sponsorUsername.trim().length < 2) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Sponsor username must be at least 2 characters long',
 type: 'error'
 };
 }

 if (sponsorUsername.trim().length > 25) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Sponsor username must be 25 characters or less',
 type: 'error'
 };
 }

 try {
 const response = await fetch(`${FORCE_API_BASE_URL}/api/check-duplicates`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 sponsorUsername: sponsorUsername.trim(),
 current_username: currentUsername?.trim()
 }),
 });

 const data: DuplicateCheckResponse = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Server error');
 }

 if (data.sponsorExists === false) {
 return {
 isValid: false,
 isChecking: false,
 message: data.sponsorError || 'Sponsor username not found. Please check the spelling.',
 type: 'error'
 };
 }

 if (data.sponsorExists === true) {
 // v4.4.0: include sponsor display name (when returned by the server) so
 // the registrant can confirm they are signing up under the correct person.
 const name = (data.sponsorName || '').trim();
 return {
 isValid: true,
 isChecking: false,
 message: name ? `Sponsor verified: ${name}` : 'Sponsor username found',
 type: 'success',
 sponsorName: name || undefined
 };
 }

 // This shouldn't happen, but handle it gracefully
 return {
 isValid: false,
 isChecking: false,
 message: 'Unable to verify sponsor username',
 type: 'error'
 };
 } catch (error) {
 console.error('Sponsor username validation error:', error);
 throw error;
 }
 }

 /**
 * Check email availability via API
 */
 private async checkEmailAvailability(email: string): Promise<ValidationResult> {
 try {
 const response = await fetch(`${FORCE_API_BASE_URL}/api/check-duplicates`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ email: email.trim().toLowerCase() }),
 });

 const data: DuplicateCheckResponse = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Server error');
 }

 if (data.emailDuplicate) {
 return {
 isValid: false,
 isChecking: false,
 message: 'Email is already registered',
 type: 'error'
 };
 }

 return {
 isValid: true,
 isChecking: false,
 message: 'Email is available',
 type: 'success'
 };
 } catch (error) {
 console.error('Email validation error:', error);
 throw error;
 }
 }

 /**
 * Basic email format validation
 */
 private isValidEmailFormat(email: string): boolean {
 return validateStrictEmail(email).valid;
 }

 /**
 * Clear validation cache
 */
 clearCache(): void {
 this.validationCache.clear();
 }

 /**
 * Clear all debounce timers
 */
 clearTimers(): void {
 this.debounceTimers.forEach(timer => clearTimeout(timer));
 this.debounceTimers.clear();
 }

 /**
 * Cleanup method to call when component unmounts
 */
 cleanup(): void {
 this.clearTimers();
 this.clearCache();
 }
}

// Export singleton instance
export const realtimeValidation = new RealtimeValidationService();
